import { Modal } from "flowbite-react";
import { LogIn, Trophy } from "lucide-react";
import { motion } from "motion/react";
import { OnboardingHeader } from "@/features/predictions/components/onboarding/OnboardingHeader";
import { H2 } from "@/shared/ui/typographies/h2";
import { P } from "@/shared/ui/typographies/paragraph";

const CONTENT_VARIANTS = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
} as const;

const CONTENT_TRANSITION = { duration: 0.18, ease: [0.16, 1, 0.3, 1] } as const;

type Props = {
  competitionName: string
  isJoining: boolean
  onDismiss: () => void
  onJoin: () => void
};

export const OptedOutModal = ({
  competitionName, isJoining, onDismiss, onJoin,
}: Props) => {
  return (
    <Modal
      show
      onClose={onDismiss}
      dismissible={false}
      size="3xl"
      className="pronostics-opted-out-modal"
    >
      <div className="flex flex-col overflow-hidden rounded-lg">
        <OnboardingHeader onClose={onDismiss} />
        <motion.div
          className="flex flex-col items-center gap-6 bg-white p-6 text-center dark:bg-gray-800 md:p-8"
          variants={CONTENT_VARIANTS}
          initial="initial"
          animate="animate"
          transition={CONTENT_TRANSITION}
        >
          <span
            aria-hidden
            className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200"
          >
            <Trophy size={28} />
          </span>
          <div className="space-y-2">
            <H2>À bientôt sur le terrain 👋</H2>
            <P color="muted" size="sm" className="mx-auto max-w-md">
              {`Tu as quitté ${competitionName}. Tu peux la rejoindre quand tu veux pour reprendre tes pronostics.`}
            </P>
          </div>
          <div className="flex flex-col items-center gap-2 pt-2">
            <button
              type="button"
              onClick={onJoin}
              disabled={isJoining}
              className="inline-flex h-11 items-center gap-2 rounded-full bg-emerald-600 px-6 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 dark:focus-visible:ring-offset-gray-800"
            >
              <LogIn size={16} aria-hidden />
              {isJoining ? "En cours…" : "Rejoindre la compétition"}
            </button>
            <button
              type="button"
              onClick={onDismiss}
              disabled={isJoining}
              className="rounded-full px-4 py-1.5 text-sm font-medium text-gray-600 transition hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 disabled:cursor-not-allowed disabled:opacity-60 dark:text-gray-300 dark:hover:text-white"
            >
              Plus tard
            </button>
          </div>
        </motion.div>
      </div>
    </Modal>
  );
};

OptedOutModal.displayName = "OptedOutModal";
